import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Upload } from "lucide-react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { BACKEND, errMsg } from "../../lib/portfolio";
import { REVIEW_LABELS } from "../../lib/phaseFeatures";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { ArchDisclaimer } from "./ArchitectGate";

const Section = ({ title, testId, children }) => (
  <Card className="ds-card border-erf-borderCream mb-5" data-testid={testId}>
    <CardContent className="p-5">
      <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg mb-3">{title}</h2>
      {children}
    </CardContent>
  </Card>
);

function Revisions({ doc, onUploaded }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const upload = (file) => {
    if (!file) return;
    setBusy(true);
    api.post(`/phase3/architect/documents/${doc.id}/revisions`, file,
      { params: { filename: file.name }, headers: { "Content-Type": file.type || "application/octet-stream" } })
      .then(() => { setError(null); onUploaded(); })
      .catch((e) => setError(errMsg(e, "Upload failed.")))
      .finally(() => { setBusy(false); if (input.current) input.current.value = ""; });
  };
  const open = (rid, variant) => api.get(`/phase3/architect/revisions/${rid}/signed-url`, { params: { variant } })
    .then((r) => window.open(`${BACKEND}${r.data.url}`, "_blank", "noopener"));
  return (
    <Section title="Revisions" testId="phase3-revisions">
      <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,.heic,.pdf" className="sr-only"
        onChange={(e) => upload(e.target.files?.[0])} data-testid="phase3-revision-input" aria-label="Choose a drawing file" />
      {doc.role === "manager" && (
        <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" disabled={busy || !!doc.archived_at}
          onClick={() => input.current?.click()} data-testid="phase3-revision-upload-btn">
          <Upload className="w-4 h-4 mr-1.5" aria-hidden="true" />{busy ? "Uploading…" : `Upload revision v${doc.current_version + 1}`}
        </Button>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-[#991B1B]" data-testid="phase3-revision-error">{error}</p>}
      <ul className="mt-3 space-y-2">
        {doc.revisions.length === 0 && <li className="text-sm font-nav text-erf-dark/80">{doc.role === "manager" ? "No revisions yet. Upload a PDF or image of the drawing." : "No revisions yet. The Founder or an Administrator will upload the drawing."}</li>}
        {doc.revisions.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm font-nav text-erf-dark" data-testid={`phase3-revision-${r.version}`}>
            <span className="mr-auto"><strong>v{r.version}</strong> · {r.filename} · {r.byte_size < 1024 ? `${r.byte_size} B` : r.byte_size < 1048576 ? `${(r.byte_size / 1024).toFixed(0)} KB` : `${(r.byte_size / 1048576).toFixed(1)} MB`} · {new Date(r.uploaded_at).toLocaleString()}</span>
            <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => open(r.id, "original")} data-testid={`phase3-revision-open-${r.version}`}>Open original</Button>
            {r.preview_content_type && (
              <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => open(r.id, "preview")} data-testid={`phase3-revision-preview-${r.version}`}>Preview</Button>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Reviews({ doc, onPosted }) {
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);
  const send = (kind) => api.post(`/phase3/architect/documents/${doc.id}/reviews`, { kind, body })
    .then(() => { setBody(""); setError(null); onPosted(); })
    .catch((e) => setError(errMsg(e, "Could not save your review.")));
  return (
    <Section title="Design review" testId="phase3-reviews">
      <ul className="space-y-2 mb-3">
        {doc.reviews.length === 0 && <li className="text-sm font-nav text-erf-dark/80">No comments yet.</li>}
        {doc.reviews.map((r) => (
          <li key={r.id} className="text-sm font-nav text-erf-dark" data-testid={`phase3-review-${r.id}`}>
            <strong>{r.author_name}</strong> on v{r.version} — {r.kind === "approve" ? "approved the design" : r.kind === "request_changes" ? "requested changes" : "commented"}
            {r.body && <>: {r.body}</>}
          </li>
        ))}
      </ul>
      <label className="text-sm font-nav text-erf-dark block">Your comment
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="mt-1" data-testid="phase3-review-body" />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => send("comment")} data-testid="phase3-review-comment">Comment</Button>
        <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => send("approve")} data-testid="phase3-review-approve">Approve design</Button>
        <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => send("request_changes")} data-testid="phase3-review-request-changes">Request changes</Button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-[#991B1B]" data-testid="phase3-review-error">{error}</p>}
    </Section>
  );
}

function StatusControl({ doc, onChanged }) {
  const [letter, setLetter] = useState(doc.status_letter);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const save = () => api.post(`/phase3/architect/documents/${doc.id}/status`, { status_letter: letter, reason })
    .then(() => { setError(null); setReason(""); onChanged(); })
    .catch((e) => setError(errMsg(e, "Could not change the status.")));
  return (
    <Section title="Document status" testId="phase3-status">
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-sm font-nav text-erf-dark">Status
          <select value={letter} onChange={(e) => setLetter(e.target.value)} data-testid="phase3-status-select"
            className="mt-1 h-11 block rounded-md border border-erf-borderCream bg-white px-3 text-sm">
            {Object.entries(doc.statuses).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
          </select>
        </label>
        <label className="text-sm font-nav text-erf-dark flex-1 min-w-[200px]">Reason
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="phase3-status-reason"
            className="mt-1 h-11 block w-full rounded-md border border-erf-borderCream bg-white px-3 text-sm" />
        </label>
        <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={save} data-testid="phase3-status-save">Save status</Button>
      </div>
      <p className="mt-2 text-xs font-nav text-erf-dark/80">Moving back to an earlier status needs the Founder and a reason of at least 10 characters.</p>
      <p className="mt-2 text-xs font-nav text-erf-dark/80">Statuses E–I (signed/sealed, permit, construction, as-built) are blocked: this workspace supports drafting and internal review and cannot produce licensed professional seals.</p>
      {error && <p role="alert" className="mt-2 text-sm text-[#991B1B]" data-testid="phase3-status-error">{error}</p>}
    </Section>
  );
}

export default function ArchitectDocument() {
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/architect/documents/${id}`).then((r) => setDoc(r.data))
    .catch((e) => setError(errMsg(e, "Could not load this drawing."))), [id]);
  useEffect(() => { load(); }, [load]);
  return (
    <AdminAppShell activeNav="architect" title={doc ? doc.title : "Drawing"} user={user}
      subtitle={doc ? `Status ${doc.status_letter} — ${doc.status_name} · ${REVIEW_LABELS[doc.review_state] || doc.review_state}` : ""}
      breadcrumbs={[{ label: "Architect Workspace", to: "/phase3/architect" }, { label: doc?.title || "Drawing", to: null }]}>
      <div data-testid="phase3-document">
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase3-document-error">{error}</p>}
        {doc && (
          <>
            <ArchDisclaimer text={doc.disclaimer} />
            {doc.role === "manager" && <StatusControl doc={doc} onChanged={load} />}
            <Revisions doc={doc} onUploaded={load} />
            <Reviews doc={doc} onPosted={load} />
          </>
        )}
      </div>
    </AdminAppShell>
  );
}
